import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';
import { FinancialStatement } from '../../../../data/service/agent-chat.service';

interface StatementRow {
  kind: 'section' | 'group' | 'account' | 'total' | 'summary';
  label: string;
  amounts: number[];
  groupKey?: string;
}

/**
 * SD-1244: a Profit and Loss or Balance Sheet in the AI chat, as a statement — sections, groups
 * and accounts, one column per period, money right-aligned in the statement's currency. Long
 * groups show their first accounts and expand on request.
 */
@Component({
  selector: 'app-chat-statement',
  templateUrl: './chat-statement.component.html',
  styleUrls: ['./chat-statement.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatStatementComponent implements OnChanges {
  static readonly CollapsedAccounts = 8;

  @Input() statement!: FinancialStatement;

  rows: StatementRow[] = [];
  periodLabels: string[] = [];
  expanded = new Set<string>();
  private hidden = new Map<string, number>();
  private formatter!: Intl.NumberFormat;

  /** The statement as rows: section title, its groups and accounts, the section total, then the summary lines. Exposed for tests. */
  static flatten(st: FinancialStatement | null | undefined): StatementRow[] {
    const rows: StatementRow[] = [];
    (st?.sections || []).forEach((section, si) => {
      rows.push({ kind: 'section', label: section.title || 'Other', amounts: [] });
      (section.groups || []).forEach((group, gi) => {
        const key = `${si}:${gi}`;
        if (group.title) {
          rows.push({ kind: 'group', label: group.title, amounts: group.total || [] });
        }
        (group.accounts || []).forEach(a => rows.push({ kind: 'account', label: a.name, amounts: a.amounts || [], groupKey: key }));
      });
      rows.push({ kind: 'total', label: `Total ${section.title || 'Other'}`, amounts: section.total || [] });
    });
    (st?.summary || []).forEach(s => rows.push({ kind: 'summary', label: s.label, amounts: s.amounts || [] }));
    return rows;
  }

  static formatterFor(currency: string | null | undefined): Intl.NumberFormat {
    try {
      if (currency && /^[A-Z]{3}$/.test(currency)) {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 2 });
      }
    } catch { /* an unknown code falls back to plain numbers */ }
    return new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  ngOnChanges(): void {
    const st = this.statement;
    this.formatter = ChatStatementComponent.formatterFor(st?.currency);
    this.periodLabels = (st?.periods || []).map((p, i) => p.label || p.asAt || p.to || (i === 0 ? 'Current' : `Period ${i + 1}`));
    this.rows = ChatStatementComponent.flatten(st);
    this.hidden.clear();
    for (const r of this.rows) {
      if (r.kind === 'account' && r.groupKey) {
        this.hidden.set(r.groupKey, (this.hidden.get(r.groupKey) || 0) + 1);
      }
    }
  }

  get isProfitAndLoss(): boolean {
    return /profit/i.test(this.statement?.report || '');
  }

  money(value: number | null | undefined): string {
    return value === null || value === undefined || isNaN(value as number) ? '—' : this.formatter.format(value);
  }

  /** Whether an account row is shown: all of a short group, the first few of a long one until expanded. */
  visible(row: StatementRow, index: number): boolean {
    if (row.kind !== 'account' || !row.groupKey || this.expanded.has(row.groupKey)) {
      return true;
    }
    if ((this.hidden.get(row.groupKey) || 0) <= ChatStatementComponent.CollapsedAccounts) {
      return true;
    }
    let position = 0;
    for (let i = 0; i < index; i++) {
      if (this.rows[i].groupKey === row.groupKey) {
        position++;
      }
    }
    return position < ChatStatementComponent.CollapsedAccounts;
  }

  /** "Show 12 more" after the last visible account of a collapsed long group. */
  moreAfter(row: StatementRow, index: number): number {
    if (row.kind !== 'account' || !row.groupKey || this.expanded.has(row.groupKey)) {
      return 0;
    }
    const count = this.hidden.get(row.groupKey) || 0;
    if (count <= ChatStatementComponent.CollapsedAccounts) {
      return 0;
    }
    let position = 0;
    for (let i = 0; i < index; i++) {
      if (this.rows[i].groupKey === row.groupKey) {
        position++;
      }
    }
    return position === ChatStatementComponent.CollapsedAccounts - 1 ? count - ChatStatementComponent.CollapsedAccounts : 0;
  }

  expand(groupKey: string | undefined): void {
    if (groupKey) {
      this.expanded.add(groupKey);
    }
  }

  trackRow(index: number): number {
    return index;
  }
}
